import { defineStore } from 'pinia';
import { unref } from 'vue';
import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';
import { getRawRoute } from '@mts-fe/utils';
import { MULTIPLE_TABS_KEY } from '@mts/enums/cacheEnum';
import { PageEnum } from '@mts/enums/pageEnum';
import { useRedo } from '@mts/hooks/web/usePage';
import { Persistent } from '@mts/utils/cache/persistent';
import { PAGE_NOT_FOUND_ROUTE, REDIRECT_ROUTE } from '@/router/routes/basic';
import { getUserStore } from '@/store';

export interface MultipleTabState {
  cacheTabList: Set<string>;
  tabList: RouteLocationNormalized[];
  lastDragEndIndex: number;
}

const EXCLUDED_PATHS = new Set([PageEnum.ERROR_PAGE, PageEnum.BASE_LOGIN, PageEnum.BASE_HOME]);
const EXCLUDED_NAMES = new Set([REDIRECT_ROUTE.name, PAGE_NOT_FOUND_ROUTE.name]);

const getToTarget = (tabItem: RouteLocationNormalized) => {
  const { params, path, query } = tabItem;
  return {
    params: params || {},
    path,
    query: query || {},
  };
};

const createPathComparator = (path: string) => {
  const decodedPath = decodeURIComponent(path);
  return (tab: RouteLocationNormalized) => decodeURIComponent(tab.path) === decodedPath;
};

let persistTimer: NodeJS.Timeout | null = null;
const debouncedPersist = (data: any) => {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    Persistent.setLocal(MULTIPLE_TABS_KEY, data);
  }, 100);
};

export const useMultipleTabStore = defineStore('app-multiple-tab', {
  state: (): MultipleTabState => ({
    // Tabs that need to be cached
    cacheTabList: new Set(),
    // multiple tab list
    tabList: Persistent.getLocal(MULTIPLE_TABS_KEY) || [],
    // Index of the last moved tab
    lastDragEndIndex: 0,
  }),
  getters: {
    getTabList(state): RouteLocationNormalized[] {
      return state.tabList;
    },
    getCachedTabList(state): string[] {
      return Array.from(state.cacheTabList);
    },
    getLastDragEndIndex(state): number {
      return state.lastDragEndIndex;
    },
  },
  actions: {
    /**
     * Update the cache according to the currently opened tabs
     */
    async updateCacheTab() {
      const cacheMap = new Set<string>();

      for (const tab of this.tabList) {
        const item = getRawRoute(tab);
        if (!item.meta?.keepAlive) continue;

        const name = item.meta.configPath || item.path;
        name && cacheMap.add(name);
      }
      this.cacheTabList = cacheMap;
    },

    /**
     * Refresh tabs
     */
    async refreshPage(router: Router) {
      const { currentRoute } = router;
      const route = unref(currentRoute);
      const name = route.path;

      if (this.cacheTabList.has(name)) {
        this.cacheTabList.delete(name);
      }
      const redo = useRedo(router);
      await redo();
    },
    clearCacheTabs(): void {
      this.cacheTabList = new Set();
    },
    resetState(): void {
      this.tabList = [];
      this.clearCacheTabs();
    },
    goToPage(router: Router) {
      const len = this.tabList.length;
      const { path } = unref(router.currentRoute);

      let toPath: PageEnum | string = PageEnum.BASE_HOME;

      if (len > 0) {
        const page = this.tabList[len - 1];
        const p = page.fullPath || page.path;
        if (p) {
          toPath = p;
        }
      }
      // Jump to the current page and report an error
      path !== toPath && router.replace(toPath as PageEnum);
    },

    async addTab(route: RouteLocationNormalized) {
      const { path, name, fullPath, params, query, meta } = getRawRoute(route);

      if (EXCLUDED_PATHS.has(path as any) || !name || EXCLUDED_NAMES.has(name as string)) {
        return;
      }

      const existingIndex = this.tabList.findIndex(createPathComparator(path));

      if (existingIndex !== -1) {
        const existingTab = this.tabList[existingIndex];
        this.tabList[existingIndex] = {
          ...existingTab,
          params: params || existingTab.params,
          query: query || existingTab.query,
          fullPath: fullPath || existingTab.fullPath,
        };
      } else {
        const dynamicLevel = meta?.dynamicLevel ?? -1;
        if (dynamicLevel > 0) {
          const realPath = meta?.realPath ?? '';
          let count = 0;
          let firstIndex = -1;
          for (let i = 0; i < this.tabList.length; i++) {
            const item = this.tabList[i];
            if ((item.meta?.realPath ?? '') === realPath) {
              count++;
              if (firstIndex === -1) firstIndex = i;
            }
          }

          if (count >= dynamicLevel && firstIndex !== -1) {
            this.tabList.splice(firstIndex, 1);
          }
        }
        this.tabList.push(route);
      }

      this.updateCacheTab();
      debouncedPersist(this.tabList);
    },

    async closeTab(tab: RouteLocationNormalized, router: Router) {
      const { currentRoute, replace } = router;
      const { path } = unref(currentRoute);

      const closeIndex = this.tabList.findIndex((item) => item.fullPath === tab.fullPath);
      if (closeIndex === -1) return;

      const tabToClose = this.tabList[closeIndex];
      if (tabToClose.meta?.affix) return;

      // 如果关闭的是非当前激活的 tab
      if (path !== tab.path) {
        this.tabList.splice(closeIndex, 1);
        this.updateCacheTab();
        return;
      }

      // 关闭当前激活的 tab
      let toTarget: RouteLocationRaw = {};
      const currentIndex = closeIndex;

      if (currentIndex === 0) {
        if (this.tabList.length === 1) {
          toTarget = getUserStore().getUserInfo.homePath;
        } else {
          toTarget = getToTarget(this.tabList[1]);
        }
      } else {
        toTarget = getToTarget(this.tabList[currentIndex - 1]);
      }

      this.tabList.splice(currentIndex, 1);
      this.updateCacheTab();
      await replace(toTarget);
    },

    // Close according to key
    async closeTabByKey(key: string, router: Router) {
      const index = this.tabList.findIndex((item) => (item.fullPath || item.path) === key);
      if (index === -1) return;

      const tabToClose = this.tabList[index];
      await this.closeTab(tabToClose, router);
    },

    // Sort the tabs
    async sortTabs(oldIndex: number, newIndex: number) {
      const currentTab = this.tabList[oldIndex];
      this.tabList.splice(oldIndex, 1);
      this.tabList.splice(newIndex, 0, currentTab);
      this.lastDragEndIndex = this.lastDragEndIndex + 1;
    },

    // Close the tab on the right and jump
    async closeLeftTabs(route: RouteLocationNormalized, router: Router) {
      const index = this.tabList.findIndex((item) => item.path === route.path);

      if (index > 0) {
        const pathList: string[] = [];
        for (let i = 0; i < index; i++) {
          const item = this.tabList[i];
          if (!item.meta?.affix) {
            pathList.push(item.fullPath);
          }
        }
        if (pathList.length > 0) {
          this.bulkCloseTabs(pathList);
        }
      }
      this.updateCacheTab();
      debouncedPersist(this.tabList);
      router.replace(router.currentRoute.value.fullPath);
    },

    // Close the tab on the left and jump
    async closeRightTabs(route: RouteLocationNormalized, router: Router) {
      const index = this.tabList.findIndex((item) => item.fullPath === route.fullPath);

      if (index >= 0 && index < this.tabList.length - 1) {
        const pathList: string[] = [];
        for (let i = index + 1; i < this.tabList.length; i++) {
          const item = this.tabList[i];
          if (!item.meta?.affix) {
            pathList.push(item.fullPath);
          }
        }
        if (pathList.length > 0) {
          this.bulkCloseTabs(pathList);
        }
      }
      this.updateCacheTab();
      debouncedPersist(this.tabList);
      router.replace(router.currentRoute.value.fullPath);
    },

    async closeAllTab(router: Router) {
      this.tabList = this.tabList.filter((item) => item.meta?.affix ?? false);
      this.clearCacheTabs();
      debouncedPersist(this.tabList);
      this.goToPage(router);
    },

    /**
     * Close other tabs
     */
    async closeOtherTabs(route: RouteLocationNormalized, router: Router) {
      const pathList: string[] = [];
      const currentPath = route.fullPath;

      for (const item of this.tabList) {
        if (item.fullPath !== currentPath && !item.meta?.affix) {
          pathList.push(item.fullPath);
        }
      }

      if (pathList.length > 0) {
        this.bulkCloseTabs(pathList);
      }
      this.updateCacheTab();
      debouncedPersist(this.tabList);
      router.replace(router.currentRoute.value.fullPath);
    },

    /**
     * Close tabs in bulk
     */
    async bulkCloseTabs(pathList: string[]) {
      if (pathList.length === 0) return;

      const pathSet = new Set(pathList);
      this.tabList = this.tabList.filter((item) => !pathSet.has(item.fullPath));
    },

    /**
     * Set tab's title
     */
    async setTabTitle(title: string, route: RouteLocationNormalized) {
      const findIndex = this.tabList.findIndex((item) => item === route);
      if (findIndex !== -1) {
        this.tabList[findIndex] = {
          ...this.tabList[findIndex],
          meta: {
            ...this.tabList[findIndex].meta,
            title,
          },
        };
        await this.updateCacheTab();
        debouncedPersist(this.tabList);
      }
    },
    /**
     * replace tab's path
     * **/
    async updateTabPath(fullPath: string, route: RouteLocationNormalized) {
      const findIndex = this.tabList.findIndex((item) => item === route);
      if (findIndex !== -1) {
        this.tabList[findIndex] = {
          ...this.tabList[findIndex],
          fullPath,
          path: fullPath,
        };
        await this.updateCacheTab();
        debouncedPersist(this.tabList);
      }
    },
  },
});
